import React from "react";
import { TanStackDevtools } from "@tanstack/react-devtools";
import { type QueryClient } from "@tanstack/react-query";
import { ReactQueryDevtoolsPanel } from "@tanstack/react-query-devtools/production";
import {
	createRootRouteWithContext,
	HeadContent,
	Outlet,
} from "@tanstack/react-router";
import { TanStackRouterDevtoolsPanelInProd as TanStackRouterDevtoolsPanel } from "@tanstack/react-router-devtools";

import type { AuthContext } from "@workspace/auth-context";
import {
	ApiHealthDevtoolPanel,
	DeadLetterDevtoolPanel,
} from "@workspace/devtools";
import { useProfile } from "@workspace/profile";
import type { useTenant } from "@workspace/tenant-context";
import { ActiveThemeProvider, ThemeProvider } from "@workspace/theme";
import { ErrorComponent } from "@workspace/ui/components/error-component";
import { Toaster } from "@workspace/ui/components/sonner";
import { TailwindIndicator } from "@workspace/ui/components/tailwind-indicator";

import { PageLoadingFull } from "@/components/page-loading";

interface AppContext {
	auth: AuthContext;
	tenants: ReturnType<typeof useTenant>;
	queryClient: QueryClient;
	profile: ReturnType<typeof useProfile>;
	timeZone: string;
}

export const Route = createRootRouteWithContext<AppContext>()({
	pendingComponent: PageLoadingFull,
	errorComponent: ErrorComponent,

	component: RouteComponent,
});

function RouteComponent() {
	const { user } = useProfile();

	const hasDevPermission = React.useMemo(() => {
		return (user as { permissions?: string[] } | null)?.permissions?.includes(
			"system.admin",
		);
	}, [user]);

	return (
		<>
			<HeadContent />
			<ThemeProvider defaultTheme="dark" storageKey="ui-theme">
				<ActiveThemeProvider initialTheme="bg" availableThemes={[]}>
					<Outlet />
					<Toaster
						toastOptions={{
							closeButton: true,
						}}
					/>
				</ActiveThemeProvider>
			</ThemeProvider>
			{hasDevPermission && (
				<TanStackDevtools
					plugins={[
						{
							name: "TanStack Query",
							render: <ReactQueryDevtoolsPanel />,
						},
						{
							name: "TanStack Router",
							render: <TanStackRouterDevtoolsPanel />,
						},
						{
							name: "API Health",
							render: <ApiHealthDevtoolPanel />,
						},
						{
							name: "Dead Letter",
							render: <DeadLetterDevtoolPanel />,
						},
					]}
				/>
			)}
			<TailwindIndicator />
		</>
	);
}
